import { Button, Flex, ProfilePhoto, Tabs, Text, Tooltip } from "@minervaproject/fds";
import { useContext, useEffect, useState } from "react";

import { VisitButtons } from "./visit_buttons";

import { PinButton } from "components/breakout_control_room/pin_button";
import {
  CenteredErrorBoundary,
  NullErrorBoundary,
  TooltipErrorBoundary,
} from "components/error_boundary";
import { ParticipantBar } from "components/participant_bar";
import { MobileCard, MobileCardHorizontalLine } from "components/shared/mobile_components";
import { BreakoutTimeRemaining } from "components/shared/timers";
import { Stage } from "components/stage";
import { ForumAPIContext } from "contexts/forum_api_context";
import { useCapabilities } from "hooks/capabilities";
import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { useIsMobile } from "hooks/use_is_mobile";
import { useSidebarAdjustment } from "hooks/use_sidebar_adjustment";
import { PROFILE_PROPERTIES, useUsersProperties } from "hooks/use_user_properties";
import {
  adjustBreakoutSessionDuration,
  assignUserToLeastPopulatedBreakoutRoom,
} from "shared/reducers/classroom";
import { ResourceTypeType } from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { UserType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";
import { createMonitoringEvent } from "store/reducers/monitoring";
import { assignNewOwnerToResource } from "utils/resource";
import { computeInitials, longDisplayName, profilePhotoUrl } from "utils/user";

const TAB_GROUP_INFO_LABELS = ["group-info", "Group Info"];
const TAB_RESOURCE_LABELS = ["resource", "Resource"];
const BREAKOUT_TAB_LABELS = [TAB_GROUP_INFO_LABELS, TAB_RESOURCE_LABELS];
const NUM_BREAKOUT_STYLES = 8;

type BreakoutRoomProps = {
  breakoutSessionId: string;
};

const RESOURCES_THAT_CAN_BE_OPENED = [
  ResourceTypeType.ForumDoc,
  ResourceTypeType.ForumWorkbook,
  ResourceTypeType.Url,
  ResourceTypeType.GoogleDrive,
  ResourceTypeType.Pdf,
];

function BreakoutRoom({ breakoutSessionId }: BreakoutRoomProps) {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const breakoutSession = useAppSelector(
    classroomSelectors.selectBreakoutSessionById(breakoutSessionId),
  );
  const { canControlClassroom, canParticipateInClass, canVisitBreakouts } = useCapabilities();
  const breakoutRoom = useAppSelector(classroomSelectors.selectActiveBreakoutRoomForCurrentUser());
  const allUsers = Object.values(
    useUsersProperties(PROFILE_PROPERTIES.concat("is_present")),
  ) as UserType[];
  const resources = useArraySelector(
    classroomSelectors.selectResourcesByIds(breakoutRoom?.resource_ids || []),
  );
  const breakoutNotes = resources.filter((resource) => resource.is_breakout_group_notes)[0];
  const canOpenBreakoutNotes = RESOURCES_THAT_CAN_BE_OPENED.includes(breakoutNotes?.type);
  const isMobile = useIsMobile();
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());
  const widthAdjustment = useSidebarAdjustment();
  const [selectedTab, setSelectedTab] = useState(TAB_GROUP_INFO_LABELS[0]);

  useEffect(() => {
    if (!breakoutRoom) return;
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.SHOW_BREAKOUT_ROOM,
        userId: currentUserId,
        contextData: { breakoutSessionId, breakoutRoom: breakoutRoom.title },
      }),
    );
    // We don't add breakoutSessionId and breakoutRoom to the dependency array because we don't want to trigger
    // multiple times the same monitoring event when the user switches between breakout rooms.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentUserId, `${breakoutSessionId}-${breakoutRoom?.title}}`]);

  const breakoutNumber = parseInt(breakoutRoom?.title || "", 10);
  const breakoutStyleNumber = breakoutNumber % NUM_BREAKOUT_STYLES;
  const assignedUsers = allUsers.filter(({ id }) => breakoutRoom?.assigned_user_ids.includes(id));

  useEffect(() => {
    if (!breakoutRoom) return;
    if (!breakoutNotes) return;
    if (!breakoutNotes.owner_id) {
      // This is necessary for whiteboards to initialize properly
      assignNewOwnerToResource(breakoutNotes, currentUserId, allUsers, dispatch, [], true, [
        breakoutRoom,
      ]);
    }
  }, [allUsers, breakoutNotes, breakoutRoom, currentUserId, dispatch]);

  useEffect(() => {
    if (canParticipateInClass && !breakoutRoom) {
      window.flightLogger.warn(
        `[BreakoutRoom] Breakout room not found for userId=${currentUserId}. User will be assigned to the least populated breakout room.`,
      );
      dispatch(assignUserToLeastPopulatedBreakoutRoom({ userId: currentUserId }));
    } else if (!breakoutRoom) {
      window.flightLogger.error(
        `[BreakoutRoom] Breakout room not found for non-student with userId=${currentUserId}`,
      );
    }
  }, [breakoutRoom, canParticipateInClass, currentUserId, dispatch]);

  if (!breakoutRoom) {
    return null;
  }

  const handleOpenNotes = async () => {
    let url = breakoutNotes.url;
    if (breakoutNotes.type === ResourceTypeType.ForumDoc) {
      url = await forumAPI.getForumDocSignedUrl(breakoutNotes.id, { readOnly: true });
    } else if (breakoutNotes.type === ResourceTypeType.ForumWorkbook) {
      url = await forumAPI.getForumWorkbookSignedUrl(breakoutNotes.id, { readOnly: true });
    }
    window.open(url, "_blank");
    window.focus();
  };

  const handleAddOneMinuteToBreakout = () => {
    dispatch(adjustBreakoutSessionDuration({ id: breakoutSessionId, timeAdjustmentInSeconds: 60 }));
  };

  if (isMobile) {
    return (
      <Flex auto direction="column" style={{ overflowY: "auto" }}>
        <Tabs
          className="pt2 px2 flex justify-center"
          kind={`dark-breakout-${breakoutStyleNumber}`}
          tabs={BREAKOUT_TAB_LABELS}
          currentTab={selectedTab}
          setCurrentTab={setSelectedTab}
        />
        {/* Rendering it but setting display to none will ensure the iframe is being loaded in the background */}
        <div style={{ display: selectedTab === TAB_RESOURCE_LABELS[0] ? "contents" : "none" }}>
          <CenteredErrorBoundary text="The stage cannot be displayed">
            <Stage panes={breakoutRoom.panes} />
          </CenteredErrorBoundary>
        </div>
        {selectedTab === TAB_GROUP_INFO_LABELS[0] && (
          <CenteredErrorBoundary text="The group info cannot be displayed">
            <Flex>
              <MobileCard className={`breakout-${breakoutStyleNumber}-card`}>
                <h1 className="h1">Group {breakoutRoom.title}</h1>
                <MobileCardHorizontalLine className={`breakout-${breakoutStyleNumber}-accent-bg`} />
                {assignedUsers.map((breakoutUser) => (
                  <Flex
                    key={breakoutUser.id}
                    className="items-center my2"
                    style={{ opacity: breakoutUser.is_present ? 1 : 0.5 }}
                  >
                    <ProfilePhoto
                      className="mr2"
                      size="sm"
                      src={profilePhotoUrl(breakoutUser)}
                      initials={computeInitials(breakoutUser)}
                    />
                    <Text kind="body-s">{longDisplayName(breakoutUser)}</Text>
                  </Flex>
                ))}
                <Button
                  kind={`primary-mobile-breakout-${breakoutStyleNumber}`}
                  className="mt6 w-full"
                  onClick={() => setSelectedTab(TAB_RESOURCE_LABELS[0])}
                >
                  View Breakout Resource
                </Button>
              </MobileCard>
            </Flex>
          </CenteredErrorBoundary>
        )}
      </Flex>
    );
  }

  return (
    <div
      className="flex flex-auto flex-column breakout-room"
      data-testid="breakout-room"
      style={{
        width: widthAdjustment,
      }}
    >
      <div className={`p2 flex items-center justify-between breakout-${breakoutStyleNumber}-card`}>
        <NullErrorBoundary>
          <Text kind="body-s" bold>
            Breakout Group {breakoutRoom.title}
            <span className="ml1" data-testid="breakout-room-users-tooltip">
              <Tooltip color="white" place="bottom">
                {assignedUsers.map((breakoutUser) => (
                  <div
                    key={breakoutUser.id}
                    className="flex items-center my2 text-black"
                    style={{ opacity: breakoutUser.is_present ? 1 : 0.5 }}
                  >
                    <ProfilePhoto
                      className="mr2"
                      size="sm"
                      src={profilePhotoUrl(breakoutUser)}
                      initials={computeInitials(breakoutUser)}
                    />
                    <Text kind="body-s">{longDisplayName(breakoutUser)}</Text>
                  </div>
                ))}
              </Tooltip>
            </span>
          </Text>
        </NullErrorBoundary>
        <div className="flex items-center">
          {canVisitBreakouts && (
            <TooltipErrorBoundary text="The breakout navigation buttons cannot be displayed">
              <VisitButtons breakoutRoom={breakoutRoom} />
            </TooltipErrorBoundary>
          )}
          {canControlClassroom && (
            <div className="ml4">
              <TooltipErrorBoundary text="The pin button cannot be displayed">
                <PinButton
                  breakoutRoom={breakoutRoom}
                  pinButtonText="Pin Group"
                  unpinButtonText="Unpin Group"
                  buttonTextClassName="text-white"
                />
              </TooltipErrorBoundary>
            </div>
          )}
          {breakoutSession.ended_on && (
            <div className="flex items-center ml4 body-s" style={{ paddingBottom: "3px" }}>
              <TooltipErrorBoundary text="The remaining time cannot be displayed">
                {canControlClassroom && (
                  <button
                    type="button"
                    className="button-reset text-white cursor-pointer mr4"
                    onClick={handleAddOneMinuteToBreakout}
                  >
                    Add 1 min
                  </button>
                )}
                <BreakoutTimeRemaining
                  breakoutSessionId={breakoutSessionId}
                  handleOpenNotes={canOpenBreakoutNotes ? handleOpenNotes : undefined}
                />
              </TooltipErrorBoundary>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-auto">
        {!isInPerson ? <ParticipantBar isVertical /> : null}
        <CenteredErrorBoundary text="The stage cannot be displayed">
          <Stage panes={breakoutRoom.panes} />
        </CenteredErrorBoundary>
      </div>
    </div>
  );
}

export { BreakoutRoom };
